import { FC, ReactNode, useEffect, useState } from 'react';

import {
  AccordionPromo,
  Button,
  Form,
  Heading,
  Icon,
  Loader,
  Text,
} from '@livechat/design-system-react-components';
import { Edit, VisibilityOn } from '@livechat/design-system-icons';

import Config from 'Config';
import { UnexpectedError } from 'components/error-message/UnexpectedError';
import { ErrorMessageForm } from 'components/forms/error-message-form/ErrorMessageForm';
import { useGetTokenDetails } from 'hooks/use-get-token-details';
import { useGetClient } from 'hooks/use-get-client';
import { ScopeGroupPermissionType } from 'types/scopes';
import { getScopeGroupsByPermissionType } from 'routes/consent/helpers';
import { submitPost } from 'helpers/form';
import Amplitude from 'services/Amplitude';
import Location from 'services/Location';
import { getAccessToken } from 'helpers/account';
import { parseJwt } from 'helpers/token';
import { ClientResponse } from 'services/connectivity/clients/types';

import * as styles from './styles';

enum ErrorType {
  ConsentCancelled = 'consent-cancelled',
  Unknown = 'unknown',
}

type FormData = {
  code: string;
  jwt: string;
  flow?: string;
};

type Scopes = {
  readOnlyScopesItems?: ReactNode[];
  readAndWriteScopesItems?: ReactNode[];
};

type TrackingData = {
  client_id: string;
  account_id: string;
  organization_id: string;
};

export const Consent: FC = () => {
  const [client, setClient] = useState<ClientResponse | null>(null);
  const [error, setError] = useState<ErrorType | null>(null);
  const [jwt, setJwt] = useState(null);
  const [scopes, setScopes] = useState<Scopes | null>(null);
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
  const [isPageOverflowing, setIsPageOverflowing] = useState<boolean>(false);

  const flow = Location.getUrlParam('flow');
  const accessToken = getAccessToken();
  const jwtString = Location.getHashParam('state');

  const { client: clientDetails } = useGetClient(jwt?.client_id);
  const { tokenDetails } = useGetTokenDetails(accessToken);

  useEffect(() => {
    if (accessToken === null || jwtString === null) {
      setError(ErrorType.Unknown);
      return;
    }

    try {
      setJwt(parseJwt(jwtString));
    } catch (e) {
      setError(ErrorType.Unknown);
    }
  }, [accessToken, jwtString]);

  useEffect(() => {
    if (error || !jwt || !tokenDetails || !clientDetails) {
      return;
    }

    const scopeList = clientDetails.scopes.map((scope) => scope.scope);
    clientDetails.features = getScopeGroupsByPermissionType(scopeList);
    setTrackingData({
      client_id: clientDetails.client_id,
      account_id: tokenDetails.account_id,
      organization_id: tokenDetails.organization_id,
    });
    setClient(clientDetails);
  }, [jwt, clientDetails, tokenDetails]);

  const getAccordionContent = (
    scopePermissionType: ScopeGroupPermissionType,
    isOpen: boolean
  ): ReactNode => (
    <div css={styles.accordionConent}>
      <div css={styles.iconAndLabel}>
        <Icon
          size="large"
          source={scopePermissionType === ScopeGroupPermissionType.View ? VisibilityOn : Edit}
        />
        <Text size="lg" bold>
          {scopePermissionType === ScopeGroupPermissionType.View ? 'View' : 'Edit'}
        </Text>
      </div>
      {!isOpen && (
        <Text css={styles.scopeGroupNames}>{getScopeGroupNames(scopePermissionType)}</Text>
      )}
    </div>
  );

  const getScopeGroupNames = (scopePermissionType: ScopeGroupPermissionType): string =>
    client?.features[scopePermissionType].map((feature) => feature.name).join(', ');

  const getScopesItems = (scopePermissionType: ScopeGroupPermissionType): ReactNode[] =>
    client?.features[scopePermissionType].map((scopeGroup) => (
      <div css={styles.singleGroupDetails}>
        <Text bold css={styles.singleGroupName}>
          {scopeGroup.name}
        </Text>
        <Text css={styles.scopeName}>{scopeGroup.scopes.join(', ')}</Text>
      </div>
    ));

  useEffect(() => {
    if (!client || error) {
      return;
    }

    setScopes({
      readOnlyScopesItems: getScopesItems(ScopeGroupPermissionType.View),
      readAndWriteScopesItems: getScopesItems(ScopeGroupPermissionType.Edit),
    });
    Amplitude.logEvent(null, `CONSENT_SCREEN_DISPLAYED`, trackingData);
  }, [client]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const resizeObserver = new ResizeObserver(() => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        const doc = document.documentElement;
        const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');

        if (isFirefox) {
          const firefoxPageHeight = doc.scrollHeight;
          const firefoxViewportHeight = doc.clientHeight;
          setIsPageOverflowing(firefoxPageHeight > firefoxViewportHeight);

          return;
        }

        const pageHeight = doc.scrollHeight;
        const viewportHeight = window.innerHeight;
        setIsPageOverflowing(pageHeight > viewportHeight);
      }, 100);
    });

    const contentContainer = document.body;

    if (contentContainer) {
      resizeObserver.observe(contentContainer);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      resizeObserver.disconnect();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData: FormData = {
      code: accessToken,
      jwt: jwtString,
    };

    if (flow === 'button') {
      formData.flow = flow;
    }

    try {
      submitPost(`${Config.accounts_api_url}/v2/clients/consent/confirm`, formData);
      Amplitude.logEvent(null, 'CONSENT_SCREEN_ALLOW_ACCESS', trackingData, true);
    } catch (requestError: any) {
      throw new Error(`Couldn't send consent confirmation, ${requestError.message}`);
    }
  };

  if (!client) {
    return (
      <div css={styles.loaderContainer}>
        <Loader size="large" />
      </div>
    );
  }

  if (error) {
    switch (error) {
      case ErrorType.ConsentCancelled: {
        return (
          <ErrorMessageForm
            titleCaption={`You didn’t grant ${client?.name} access to your account`}
            titleCaptionSize="md"
            titleContent={`To use ${client?.name}, go back to the permissions page and allow access.`}
          >
            <Button kind="link-light" onClick={() => setError(null)} css={styles.goBackButton}>
              <b>Back to permissions</b>
            </Button>
          </ErrorMessageForm>
        );
      }
      default:
        return <UnexpectedError />;
    }
  }

  if (!client) {
    return null;
  }

  return (
    <div css={styles.mainContainer(isPageOverflowing)}>
      <Heading size="md" bold css={styles.header}>
        {client?.name} is requesting permission to access your account
      </Heading>
      {client && client?.scopes.length > 0 && (
        <Text css={styles.consentNote}>You will give the app’s developer access to:</Text>
      )}
      <Form onSubmit={handleSubmit}>
        <div css={styles.accordionContainer}>
          {!!scopes?.readOnlyScopesItems.length && (
            <AccordionPromo
              label={{
                closed: getAccordionContent(ScopeGroupPermissionType.View, false),
                open: getAccordionContent(ScopeGroupPermissionType.View, true),
              }}
              css={styles.accordion}
            >
              <div css={styles.scopesGroupsDetails}>{scopes?.readOnlyScopesItems}</div>
            </AccordionPromo>
          )}
          {!!scopes?.readAndWriteScopesItems.length && (
            <AccordionPromo
              label={{
                closed: getAccordionContent(ScopeGroupPermissionType.Edit, false),
                open: getAccordionContent(ScopeGroupPermissionType.Edit, true),
              }}
              css={styles.accordion}
            >
              <div css={styles.scopesGroupsDetails}>{scopes?.readAndWriteScopesItems}</div>
            </AccordionPromo>
          )}
        </div>

        <div css={styles.buttonsContainer(isPageOverflowing)}>
          <Button kind="secondary" onClick={() => setError(ErrorType.ConsentCancelled)}>
            Cancel
          </Button>
          <Button kind="primary" type="submit">
            Allow
          </Button>
        </div>
      </Form>
    </div>
  );
};
